// Blog data layer — backed by Sanity CMS.
// Keeps the same shape the components already consume.
import {
  getPublishedPosts as sanityGetPublishedPosts,
  getRecentPosts as sanityGetRecentPosts,
  getPostBySlug as sanityGetPostBySlug,
  type SanityBlogPostListItem,
  type SanityBlogPostFull,
  type BlogCategory,
} from "@/lib/sanity";

export type { BlogCategory };

export interface BlogPostListItem {
  id: string;
  slug: string;
  category: BlogCategory;
  cover_image_url: string;
  title_en: string;
  title_es: string;
  excerpt_en: string;
  excerpt_es: string;
  read_minutes: number;
  published_at: string;
}

export interface BlogPostFull extends BlogPostListItem {
  content_en: string;
  content_es: string;
}

function adaptList(p: SanityBlogPostListItem): BlogPostListItem {
  return {
    id: p._id,
    slug: p.slug,
    category: p.category,
    cover_image_url: p.coverImageUrl,
    title_en: p.title_en,
    title_es: p.title_es,
    excerpt_en: p.excerpt_en,
    excerpt_es: p.excerpt_es,
    read_minutes: p.readMinutes,
    published_at: p.publishedAt,
  };
}

function adaptFull(p: SanityBlogPostFull): BlogPostFull {
  return { ...adaptList(p), content_en: p.content_en, content_es: p.content_es };
}

export async function getPublishedPosts(): Promise<BlogPostListItem[]> {
  try {
    const data = await sanityGetPublishedPosts();
    return data.map(adaptList);
  } catch (e) {
    console.error("getPublishedPosts error:", e);
    return [];
  }
}

export async function getRecentPosts(limit = 3): Promise<BlogPostListItem[]> {
  try {
    const data = await sanityGetRecentPosts(limit);
    return data.map(adaptList);
  } catch (e) {
    console.error("getRecentPosts error:", e);
    return [];
  }
}

export async function getPostBySlug(
  slug: string,
): Promise<{ post: BlogPostFull | null; related: BlogPostListItem[] }> {
  try {
    const { post, related } = await sanityGetPostBySlug(slug);
    return {
      post: post ? adaptFull(post) : null,
      related: related.map(adaptList),
    };
  } catch (e) {
    console.error("getPostBySlug error:", e);
    return { post: null, related: [] };
  }
}
